import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Input, Button } from '@klaro7/ui';

import { extractErrorMessage } from '@/lib/api';
import { SOCIAL_PLATFORM_ICONS, SOCIAL_PLATFORM_LABELS } from '@/components/SocialLinks';
import {
  useSocialSettings,
  useUpdateSocialSettings,
} from '@/features/settings/hooks/useServicesSettings';
import { SOCIAL_PLATFORMS, type SocialPlatform, type SocialSettings } from '@/features/settings/services/settings.service';

const EMPTY_SETTINGS: SocialSettings = SOCIAL_PLATFORMS.reduce((acc, platform) => {
  acc[platform] = { enabled: false, url: '' };
  return acc;
}, {} as SocialSettings);

export function AdminSocialPage() {
  const { data: settings, isLoading } = useSocialSettings(true);
  const updateMutation = useUpdateSocialSettings();

  const [values, setValues] = useState<SocialSettings>(EMPTY_SETTINGS);

  useEffect(() => {
    if (settings) setValues(settings);
  }, [settings]);

  function setPlatform(platform: SocialPlatform, patch: Partial<SocialSettings[SocialPlatform]>) {
    setValues((prev) => ({ ...prev, [platform]: { ...prev[platform], ...patch } }));
  }

  async function handleSave() {
    try {
      await updateMutation.mutateAsync(values);
      toast.success('Social salvati con successo');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Social</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Icone social mostrate nel footer della home. Attiva il canale e inserisci il link per pubblicarlo.
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <h3 className="text-sm font-semibold text-neutral-700">Canali social</h3>
          <p className="text-xs text-neutral-600">
            Lo switch spegne l'icona in home senza perdere il link salvato.
          </p>
        </div>

        {isLoading ? (
          <div className="p-5 text-sm text-neutral-500">Caricamento…</div>
        ) : (
          <div className="divide-y divide-neutral-100 p-5">
            {SOCIAL_PLATFORMS.map((platform) => {
              const Icon = SOCIAL_PLATFORM_ICONS[platform];
              const label = SOCIAL_PLATFORM_LABELS[platform];
              const config = values[platform] ?? { enabled: false, url: '' };
              const showHint = config.enabled && config.url.trim() === '';

              return (
                <div key={platform} className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-start">
                  <div className="flex w-full items-center gap-3 sm:w-56 sm:shrink-0">
                    <Icon className="h-5 w-5 shrink-0" />
                    <span className="text-sm font-medium text-neutral-800">{label}</span>
                    <label className="ml-auto flex items-center gap-2 text-xs text-neutral-600 sm:ml-3">
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={(e) => setPlatform(platform, { enabled: e.target.checked })}
                        className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
                        aria-label={`Attiva ${label}`}
                      />
                      Attivo
                    </label>
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder={`https://...`}
                      value={config.url}
                      disabled={!config.enabled}
                      onChange={(e) => setPlatform(platform, { url: e.target.value })}
                    />
                    {showHint && (
                      <p className="mt-1 text-xs text-amber-600">
                        Inserisci il link per mostrare l'icona in home
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end border-t border-neutral-100 p-5">
          <Button onClick={handleSave} loading={updateMutation.isPending}>
            Salva
          </Button>
        </div>
      </div>
    </div>
  );
}
